import React from "react";
import { useTranslation } from "react-i18next";
import type { FieldConfig, FieldInputProps } from "formik";
import type { Collection } from "../../types/form.types";
import formStyles from "../../features/MainForm/Form.module.css";
import styles from "./Dropdown.module.css";
import classNames from "classnames";

interface DropdownSelectProps {
  label: string;
  id: string;
  name: string;
  options: Collection[];
  getFieldProps: (
    nameOrOptions: string | FieldConfig<any>
  ) => FieldInputProps<any>;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  touched?: { [key: string]: boolean | boolean[] };
  errors?: { [key: string]: string | string[] };
}

export const Dropdown: React.FC<DropdownSelectProps> = ({
  label,
  id,
  name,
  options,
  getFieldProps,
  setFieldValue,
  touched,
  errors,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedOptions, setSelectedOptions] = React.useState<string[]>([]);
  const formikProps = getFieldProps(name);

  const toggleDropdown = () => setIsOpen(!isOpen);
  const handleSelectChange = (optionValue: string) => {
    if (selectedOptions.includes(optionValue)) {
      const updatedOptions = selectedOptions.filter(
        (item) => item !== optionValue
      );
      setSelectedOptions(updatedOptions);
      setFieldValue(name, updatedOptions);
    } else {
      const newSelectedOptions = [...selectedOptions, optionValue];
      setSelectedOptions(newSelectedOptions);
      setFieldValue(name, newSelectedOptions);
    }
  };

  const handleRemoveOption = (option: string) => {
    const updatedOptions = selectedOptions.filter((item) => item !== option);
    setSelectedOptions(updatedOptions);
    setFieldValue(name, updatedOptions);
  };

  return (
    <div className={formStyles.formGroup}>
      <label htmlFor={id} className={formStyles.labelGroup}>
        {label}
      </label>

      <div className={styles.dropdownSelect}>
        <div
          onClick={toggleDropdown}
          className={classNames(styles.dropdownHeader, {
            [styles.dropdownHeaderError]: touched?.[name] && errors?.[name],
          })}
        >
          {selectedOptions.length > 0
            ? `${selectedOptions[0]} y ${selectedOptions.length} más`
            : t("field.select.placeholder")}
        </div>

        {isOpen && (
          <div className={styles.dropdownBody}>
            {options.map((option) => (
              <label key={option.id} className={styles.dropdownOption}>
                <input
                  type="checkbox"
                  {...formikProps}
                  checked={selectedOptions.includes(option.value)}
                  onChange={() => handleSelectChange(option.value)}
                />
                {option.value}
              </label>
            ))}
          </div>
        )}
      </div>

      {touched?.[name] && errors?.[name] && (
        <p className={formStyles.formError}>{errors[name]}</p>
      )}

      <div className={styles.dropdownOptions}>
        {selectedOptions.map((option) => (
          <div key={option} className={styles.badge} role="listitem">
            {option}{" "}
            <button type="button" onClick={() => handleRemoveOption(option)}>
              X
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
